import {FunctionComponent, ReactElement, useEffect, useState} from "react";
import {Grid, useMediaQuery, useTheme} from "@mui/material";
import {ACard, Skeleton,ProgressCard} from "@atiautomacao/ati-ui-library";
import {FindInstantDataByPowerStationReactQuery} from "./InstantDataReactQuery";
import {truncateNumber} from "../../../../Utils/NumberUtil";
import {useAppSelector} from "../../../../Config/Hooks";

const InstantData : FunctionComponent<{ powerPlant: any; }> = ({powerPlant}) : ReactElement => {
    const theme = useTheme();
    const largeUpScreen = useMediaQuery(theme.breakpoints.only("lg"));
    const extraLargeUpScreen = useMediaQuery(theme.breakpoints.only("xl"));

    const {data, isLoading} = FindInstantDataByPowerStationReactQuery(powerPlant.id)
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const [energy, setEnergy] = useState<number | undefined>(undefined)
    const [maxEnergy, setMaxEnergy] = useState<number>(0)
    const [irradiance, setIrradiance] = useState<number | undefined>(undefined)
    const [maxIrradiance, setMaxIrradiance] = useState<number>(0)
    const [meteorologicalStation, setMeteorologicalStation] = useState<any[]>([])

    let targetNames = [
        {name: "Temperatura do Módulo", maxValue: 80, unit: "ºC", value: "-", barInterval: {min: {value: 26, color: "#FFD700"}, mid: {value: 53, color:"#a4f657"}, max: {value: 80, color: "#FF4500"}}},
        {name: "Umidade do Ar", maxValue: 100, unit: "%", value: "-",  barInterval: {min: {value: 0, color:"#a4f657"}, mid: {value: 0, color:"#a4f657"}, max: {value: 0, color:"#a4f657"}}},
        {name: "Velocidade do Vento", maxValue: 14, unit: "m/s", value: "-", barInterval: {min: {value: truncateNumber(Number(14/3),2), color: "#a4f657"}, mid: {value: 11, color: "#FFD700"}, max: {value: 14, color: "#FF4500"}}},
        {name: "Temperatura do Ar", maxValue: 80, unit: "ºC", value: "-", barInterval: {min: {value: 26, color: "#a4f657"}, mid: {value: 53, color:"#FFD700"}, max: {value: 80, color: "#FF4500"}}}
    ];
    
    useEffect(() => {
        setEnergy(undefined)
        setIrradiance(undefined)
    },[powerPlant])

    useEffect(() => {
        if(data?.status === 200){
            if(data.data.expectedEnergy !== null && data.data.activePower !== null){
                setEnergy(truncateNumber(data.data.activePower,2))
                setMaxEnergy(truncateNumber(data.data.expectedEnergy,2))
            }
            if(data.data.expectedIrradiance !== null && data.data.irradiance !== null){
                setIrradiance(truncateNumber(data.data.irradiance,2))
                setMaxIrradiance(truncateNumber(data.data.expectedIrradiance,2))
            }
            const filteredStations = data.data.meteorologicalStation.filter((station: any) =>
                targetNames.some(target => target.name.includes(station.name))
            );

            let stationsWithMaxValue = filteredStations.map((station:any) => {

                const target = targetNames.find(target => target.name.includes(station.name));
                let shortName
                
                if(target?.name !== undefined){
                    if(target.name.startsWith("Temperatura")){
                        shortName = target.name.replace("Temperatura", "Temp.");
                    }else if(target.name.startsWith("Umidade")){
                        shortName = target.name.replace("Umidade", "Umid.");
                    }
                } 
                return {
                    ...station,
                    name:  shortName ?? target?.name,
                    maxValue: target ? target.maxValue : undefined, 
                    unit: target ? target.unit : undefined, 
                    barInterval: target ? target.barInterval : undefined
                    
                };
            });
            
            if(stationsWithMaxValue.length < 4) {

                let missingStations = targetNames.filter(target => !stationsWithMaxValue.some((station:any) => station.name === target.name))
                missingStations = missingStations.map((target) => {
                    let shortName;
                    if(target?.name !== undefined){
                        if(target.name.startsWith("Temperatura")){
                            shortName = target.name.replace("Temperatura", "Temp.");
                        }else if(target.name.startsWith("Umidade")){
                            shortName = target.name.replace("Umidade", "Umid.");
                        }
                    }
                    return {
                        ...target,
                        name: shortName ?? target.name
                    };
                });
                stationsWithMaxValue = [...stationsWithMaxValue, ...missingStations];
            }
            stationsWithMaxValue = stationsWithMaxValue.filter((station: any) => station.maxValue !== undefined);
            setMeteorologicalStation(stationsWithMaxValue.length > 0 ? stationsWithMaxValue : targetNames)
        }else{
            targetNames = [
                {name: "Temp. do Módulo", maxValue: 80, unit: "ºC", value: "-", barInterval: {min: {value: 26, color: "#FFD700"}, mid: {value: 53, color:"#a4f657"}, max: {value: 80, color: "#FF4500"}}},
                {name: "Umid. do Ar", maxValue: 100, unit: "%", value: "-", barInterval:  {min: {value: 0, color:"#a4f657"}, mid: {value: 0, color:"#a4f657"}, max: {value: 0, color:"#a4f657"}}},
                {name: "Velocidade do Vento", maxValue: 14, unit: "m/s", value: "-", barInterval: {min: {value: truncateNumber(Number(14/3),2), color: "#a4f657"}, mid: {value: 11, color: "#FFD700"}, max: {value: 14, color: "#FF4500"}}},
                {name: "Temp. do Ar", maxValue: 80, unit: "ºC", value: "-", barInterval: {min: {value: 26, color: "#a4f657"}, mid: {value: 53, color:"#FFD700"}, max: {value: 80, color: "#FF4500"}}}
            ]
            setMeteorologicalStation(targetNames)
            setEnergy(undefined)
            setIrradiance(undefined)
        }
    }, [data]);

    return (
        <ACard
            title=""
            ariaLabel={'dashboard-station-instant-data'}
            styleProps={{
                cardStyle: { height: (largeUpScreen || extraLargeUpScreen) ? 598 : undefined,
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                 },
                contentStyle: { padding: '0px', margin: '0px', paddingTop: 1, paddingBottom: 2 },
                headerStyle: {
                    display: 'none',
                },
            }}
        >
            <Grid
                container
                spacing={{ xs: '12px' }}
                justifyContent="center"
                alignItems="center"
                style={{
                    height: (largeUpScreen || extraLargeUpScreen) ? 575 : undefined
                }}
            >
                { isLoading ?
                  <>{Array.from({ length: 6 }).map((_, index) => {
                      return (
                            <Grid aria-label={'dashboard-station-power-instant-data'} xs={6} sm={6} md={4} lg={12} xl={12} key={index.valueOf()}  style={{
                height: (largeUpScreen || extraLargeUpScreen) ? 85 : undefined
            }}>
                                <Skeleton key={index.valueOf()} animation="wave" height={(largeUpScreen || extraLargeUpScreen) ? 120 : 30} variant="rounded" width={'100%'} />
                            </Grid>
                      );
                  })}</>

                :
                <>
                  <Grid aria-label={'dashboard-station-power-instant-data'} item xs={6} sm={6} md={4} lg={12} xl={12}>
                        <ProgressCard
                            value={energy ?? "-"}
                            fontSize={16}
                            valueFontSize={15}
                            maxValue={maxEnergy}
                            maxWidthFont={openSubMenu ? {xs: 40, sm: 100} : {xs: 80, sm: 100}}
                            label="Potência"
                            unit={"kW"}
                            background="#F9F9F9"
                            textColor="#000000"
                            width={'100%'}
                            boldLabel={true}
                            flexWrap={openSubMenu}
                            isPercentValue={false}
                            styledPaperProps={{ marginLeft:-1,width: '100%' }}
                            intervals={{min: {value: maxEnergy * 0.33, color: "#FF4500"}, mid: {value: maxEnergy * 0.66, color: "#FFD700"}, max: {value: maxEnergy * 1.1, color: "#a4f657"}}}
                        />
                    </Grid>
                    <Grid aria-label={'dashboard-station-irradiance-instant-data'} item xs={6} sm={6} md={4} lg={12} xl={12}>
                        <ProgressCard
                            value={irradiance ?? "-"}
                            fontSize={16}
                            valueFontSize={15}
                            maxWidthFont={openSubMenu ? {xs: 40, sm: 100} : {xs: 80, sm: 100}}
                            maxValue={1000}
                            label="Irradiância"
                            unit={"W/m²"}
                            background="#F9F9F9"
                            textColor="#000000"
                            width={'100%'}
                            boldLabel={true}
                            flexWrap={openSubMenu}
                            isPercentValue={false}
                            styledPaperProps={{ marginLeft:-1, width: '100%' }}
                            intervals={{min: {value:528, color: "#FF4500"}, mid: {value: 1056, color: "#FFD700"}, max: {value: 1600, color: "#a4f657"}}}
                        />
                    </Grid>
                    {meteorologicalStation && meteorologicalStation.length > 0 &&
                        meteorologicalStation.map((station, index) => {
                            const measureValue = station.value == null || isNaN(Number(station.value))
                                ? "-"
                                : truncateNumber(station.value, 2);
                            return (
                            <Grid item aria-label={`${index}-dashboard-station-meteorological-instant-data`} xs={6} sm={6} md={4} lg={12} xl={12} key={station.id}>
                                <ProgressCard
                                    value={measureValue}
                                    fontSize={16}
                                    valueFontSize={15}
                                    maxWidthFont={openSubMenu ? {xs: 40, sm: 90} : {xs: 80, sm: 100}}
                                    maxValue={station.maxValue}
                                    unit={station.unit}
                                    label={station.name}
                                    background="#F9F9F9"
                                    textColor="#000000"
                                    width={'100%'}
                                    boldLabel={true}
                                    flexWrap={openSubMenu}
                                    isPercentValue={false}
                                    intervals={station.barInterval}
                                    styledPaperProps={{ marginLeft: -1, width: '100%', backgroundColor: '#F9F9F9' }}
                                />
                            </Grid>
                            );
                        })
                    }
                </>
              
                }
                </Grid>

        </ACard>
    );

}

export default InstantData;